/**
 * DanaVoice - Analytics Service
 * 
 * API client for analytics endpoints.
 * Provides methods for fetching call volume, performance metrics, and reports.
 */

import { apiClient } from './api-client';

/**
 * Time series data point
 */
export interface TimeSeriesDataPoint {
  timestamp: string;
  total_calls: number;
  ai_handled: number;
  escalated: number;
  messages_taken: number;
}

/**
 * Call volume analytics response
 */
export interface CallVolumeAnalytics {
  time_series: TimeSeriesDataPoint[];
  summary: {
    total_calls: number;
    ai_success_rate: number;
    average_duration_seconds: number;
    call_type_distribution: Record<string, number>;
  };
}

/**
 * Call type distribution
 */
export interface CallTypeDistribution {
  call_type: string;
  count: number;
  percentage: number;
}

/**
 * Peak hour data
 */
export interface PeakHourData {
  hour: number;
  total_calls: number;
  avg_duration_seconds: number;
  ai_handled: number;
  escalated: number;
}

/**
 * Performance metrics
 */
export interface PerformanceMetrics {
  total_calls: number;
  ai_handled_count: number;
  escalated_count: number;
  messages_taken_count: number;
  ai_success_rate: number;
  escalation_rate: number;
  avg_duration_seconds: number;
  median_duration_seconds: number;
  total_duration_minutes: number;
}

/**
 * Weekly report
 */
export interface WeeklyReport {
  week_start: string;
  week_end: string;
  daily_breakdown: Array<{
    date: string;
    day_of_week: string;
    total_calls: number;
    ai_handled: number;
    escalated: number;
    avg_duration_seconds: number;
  }>;
  week_totals: {
    total_calls: number;
    ai_success_rate: number;
    escalation_rate: number;
    avg_duration_seconds: number;
  };
  top_call_types: Array<{
    call_type: string;
    count: number;
  }>;
  peak_day: {
    date: string;
    total_calls: number;
  };
}

/**
 * Recent call log
 */
export interface RecentCall {
  id: string;
  call_type: string;
  outcome: string;
  duration_seconds: number | null;
  caller_phone: string | null;
  resident_id: string | null;
  created_at: string;
}

/**
 * Dashboard summary
 */
export interface DashboardSummary {
  today: {
    total_calls: number;
    ai_handled: number;
    escalated: number;
    ai_success_rate: number;
  };
  last_7_days: {
    total_calls: number;
    ai_success_rate: number;
    avg_duration_seconds: number;
  };
  recent_calls: RecentCall[];
}

const LOCATION = import.meta.env.VITE_LOCATION || 'dana';

/**
 * Analytics Service
 */
export const analyticsService = {
  /**
   * Get call volume analytics
   */
  async getCallVolume(
    fromDate: Date,
    toDate: Date,
    groupBy: 'hour' | 'day' | 'week' = 'hour'
  ): Promise<CallVolumeAnalytics> {
    const params = new URLSearchParams({
      from_date: fromDate.toISOString(),
      to_date: toDate.toISOString(),
      groupBy,
      location: LOCATION,
    });

    const response = await apiClient.get(`/analytics/call-volume?${params}`);
    return response.data;
  },

  /**
   * Get call types distribution
   */
  async getCallTypes(
    fromDate: Date,
    toDate: Date
  ): Promise<CallTypeDistribution[]> {
    const params = new URLSearchParams({
      from_date: fromDate.toISOString(),
      to_date: toDate.toISOString(),
      location: LOCATION,
    });

    const response = await apiClient.get(`/analytics/call-types?${params}`);
    return response.data;
  },

  /**
   * Get peak hours analysis
   */
  async getPeakHours(
    fromDate: Date,
    toDate: Date
  ): Promise<PeakHourData[]> {
    const params = new URLSearchParams({
      from_date: fromDate.toISOString(),
      to_date: toDate.toISOString(),
      location: LOCATION,
    });

    const response = await apiClient.get(`/analytics/peak-hours?${params}`);
    return response.data;
  },

  /**
   * Get performance metrics
   */
  async getPerformance(
    fromDate: Date,
    toDate: Date
  ): Promise<PerformanceMetrics> {
    const params = new URLSearchParams({
      from_date: fromDate.toISOString(),
      to_date: toDate.toISOString(),
      location: LOCATION,
    });

    const response = await apiClient.get(`/analytics/performance?${params}`);
    return response.data;
  },

  /**
   * Get weekly report
   */
  async getWeeklyReport(weekStart: Date): Promise<WeeklyReport> {
    const params = new URLSearchParams({
      week_start: weekStart.toISOString().split('T')[0],
      location: LOCATION,
    });

    const response = await apiClient.get(`/analytics/weekly-report?${params}`);
    return response.data;
  },

  /**
   * Get recent calls
   */
  async getRecentCalls(
    limit: number = 50,
    offset: number = 0,
    filters?: {
      call_type?: string;
      outcome?: string;
      resident_id?: string;
    }
  ): Promise<RecentCall[]> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
      location: LOCATION,
    });

    if (filters?.call_type) {
      params.append('call_type', filters.call_type);
    }
    if (filters?.outcome) {
      params.append('outcome', filters.outcome);
    }
    if (filters?.resident_id) {
      params.append('resident_id', filters.resident_id);
    }

    const response = await apiClient.get(`/analytics/recent-calls?${params}`);
    return response.data;
  },

  /**
   * Get dashboard summary
   */
  async getDashboardSummary(): Promise<DashboardSummary> {
    const response = await apiClient.get(`/analytics/dashboard-summary?location=${LOCATION}`);
    return response.data;
  },
};